import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, userService } from '../api/apiService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in (token exists in localStorage)
    const token = localStorage.getItem('token');
    const savedUserRole = localStorage.getItem('userRole');
    
    // console.log('Checking authentication state on load:');
    // console.log('- Token exists:', !!token);
    // console.log('- Token exists:', token);
    // console.log('- Saved user role:', savedUserRole);
    
    if (token) {
      // Get user profile from the API
      userService.getProfile()
        .then(response => {
          // console.log('User profile retrieved successfully:', response.data);
          // Extract user data from the response structure
          const userData = response.data.user || response.data;
          
          // Save filter preferences to localStorage for offline access
          if (userData.filterPreferences) {
            localStorage.setItem(`filterPreferences_${userData.email}`, 
              JSON.stringify(userData.filterPreferences));
          }
          
          if (userData.calendarFilterPreferences) {
            localStorage.setItem(`calendarFilterPreferences_${userData.email}`, 
              JSON.stringify(userData.calendarFilterPreferences));
          }
          
          setCurrentUser(userData);
          
          // Get role from the correct location in the response
          const userRoleFromResponse = userData.role;
          // console.log('Role from API response:', userRoleFromResponse);
          
          // Set role with priority: API response > saved role > default null
          const roleToUse = userRoleFromResponse || savedUserRole || null;
          setUserRole(roleToUse);
          
          // Always update localStorage with the current role
          if (roleToUse) {
            localStorage.setItem('userRole', roleToUse);
            // console.log('Updated localStorage with role:', roleToUse);
          }
        })
        .catch(error => {
          // If token is invalid or expired, clear localStorage
          console.error('Failed to get user profile:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('userRole');
          setCurrentUser(null);
          setUserRole(null);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      setError('');
      // console.log('Attempting login for:', email);
      const response = await authService.login({ email, password });
      
      // console.log('Login successful, received token:', !!response.data.token);
      
      // Save token to localStorage
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('isAuthenticated', 'true');
      
      // Get user profile
      const userResponse = await userService.getProfile();
      // console.log('User profile data:', userResponse.data);
      
      // Extract user data from the response structure
      const userData = userResponse.data.user || userResponse.data;
      
      // Save filter preferences to localStorage for offline access
      if (userData.filterPreferences) {
        localStorage.setItem(`filterPreferences_${userData.email}`, 
          JSON.stringify(userData.filterPreferences));
      }
      
      if (userData.calendarFilterPreferences) {
        localStorage.setItem(`calendarFilterPreferences_${userData.email}`, 
          JSON.stringify(userData.calendarFilterPreferences));
      }
      
      setCurrentUser(userData);
      
      // Get role from the user data
      const userRoleFromResponse = userData.role;
      setUserRole(userRoleFromResponse);
      
      // Save user role to localStorage
      localStorage.setItem('userRole', userRoleFromResponse);
      localStorage.setItem('userEmail', userData.email);
      
      // console.log('User authenticated with role:', userRoleFromResponse);
      
      // Navigate based on user role
      switch (userRoleFromResponse) {
        case 'superadmin':
        case 'setCastEditor':
          navigate('/admin');
          break;
        case 'editor':
          navigate('/user');
          break;
        case 'talent':
          navigate('/talent');
          break;
        case 'clientService':
        case 'planning':
          navigate('/cs');
          break;
        case 'finance':
          navigate('/finance');
          break;
        default:
          navigate('/np');
      }
      
      return userData;
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error details:', error.response?.data || 'No response data');
      setError(error.response?.data?.message || 'Failed to login. Please check your credentials.');
      throw error;
    }
  };

  const logout = async () => {
    try {
      // console.log('Logging out user');
      
      // Clear all filter preferences
      if (currentUser?.email) {
        localStorage.removeItem(`filterPreferences_${currentUser.email}`);
        localStorage.removeItem(`calendarFilterPreferences_${currentUser.email}`);
      }
      
      // Clear local storage
      localStorage.removeItem('token');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userEmail');
      
      // Reset state
      setCurrentUser(null);
      setUserRole(null);
      
      // console.log('Logout successful, auth state cleared');
      
      // Redirect to login page
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      setError('Failed to logout.');
      throw error;
    }
  };

  const updateUserPreferences = async (preferences) => {
    try {
      if (!currentUser) throw new Error('User not logged in');
      
      // Update user profile in the API
      const response = await userService.updateProfile(preferences);
      
      // Update current user state with new preferences
      const updatedUser = { ...currentUser, ...preferences };
      setCurrentUser(updatedUser);
      
      // Update localStorage for offline access
      if (preferences.filterPreferences) {
        localStorage.setItem(`filterPreferences_${currentUser.email}`, 
          JSON.stringify(preferences.filterPreferences));
      }
      
      if (preferences.calendarFilterPreferences) {
        localStorage.setItem(`calendarFilterPreferences_${currentUser.email}`, 
          JSON.stringify(preferences.calendarFilterPreferences));
      }
      
      return response.data;
    } catch (error) {
      console.error('Error updating user preferences:', error);
      throw error;
    }
  };

  const forgotPassword = async (email) => {
    try {
      setError('');
      await authService.forgotPassword(email);
      return true;
    } catch (error) {
      console.error('Forgot password error:', error);
      setError(error.response?.data?.message || 'Failed to send password reset email.');
      throw error;
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      setError('');
      await authService.resetPassword(token, newPassword);
      return true;
    } catch (error) {
      console.error('Reset password error:', error);
      setError(error.response?.data?.message || 'Failed to reset password.');
      throw error;
    }
  };

  const value = {
    currentUser,
    userRole,
    login,
    logout,
    forgotPassword,
    resetPassword,
    updateUserPreferences,
    error,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export default AuthContext; 